import { isObject } from '@shared/utils/isObject';

/**
 * Resolves the chain of iterate steps from a given step back to the original non-iterate data source.
 * Returns the chain of iterate steps with their dataKey and activeIndex configurations.
 *
 * @param {string} stepId - The starting step ID (usually an iterate step)
 * @param {Array} steps - All workflow steps
 * @param {Array} apps - All apps metadata
 * @returns {Object} { chain: Array, originalStepId: string }
 *   - chain: Array of { stepId, dataKey, activeIndex } for each iterate step in the chain
 *   - originalStepId: The ID of the non-iterate data source step
 */
export const resolveIterateChain = (stepId, steps, apps) => {
  const chain = [];
  let currentStepId = stepId;

  // Build lookup maps for O(1) access instead of O(n) .find() calls
  const stepsById = {};
  const appSlugsById = {};

  if (steps) {
    for (let i = 0; i < steps.length; i++) {
      stepsById[steps[i].id] = steps[i];
    }
  }

  if (apps) {
    for (let i = 0; i < apps.length; i++) {
      appSlugsById[apps[i].id] = apps[i].slug;
    }
  }

  while (currentStepId) {
    const step = stepsById[currentStepId];
    if (!step) {
      break;
    }

    const isIterate = appSlugsById[step.appId] === 'iterate';

    if (isIterate) {
      chain.push({
        stepId: currentStepId,
        dataKey: step.arguments?.dataKey || '',
        activeIndex: step.arguments?.activeIndex || 0
      });
      currentStepId = step.arguments?.stepId;
    } else {
      // Found the original non-iterate data source
      // Reverse the chain so it goes from outer (closest to source) to inner (current step)
      return { chain: chain.reverse(), originalStepId: currentStepId };
    }
  }

  // Reverse the chain so it goes from outer to inner
  return { chain: chain.reverse(), originalStepId: null };
};

/**
 * Drills down through nested data using a chain of iterate configurations.
 * Each level applies activeIndex and dataKey to navigate to the correct nested data.
 *
 * @param {Object} output - The original data source output (with .data array)
 * @param {Array} chain - Array of { dataKey, activeIndex } from resolveIterateChain
 * @param {Object} options - Options for drilling
 * @param {boolean} options.stopBeforeLast - If true, stops before applying the last chain item's dataKey
 * @returns {*} The drilled-down data at the specified nesting level
 */
export const drillDownNestedData = (output, chain, options = {}) => {
  const { stopBeforeLast = false } = options;

  if (!output?.data || !Array.isArray(output.data) || output.data.length === 0) {
    return undefined;
  }

  // Start with the full output data array
  let currentData = output.data;

  // Process each level of the chain
  const levels = stopBeforeLast ? chain.slice(0, -1) : chain;

  for (let i = 0; i < levels.length; i++) {
    const { dataKey, activeIndex } = levels[i];

    if (dataKey) {
      // With dataKey: collapse array to first item to get the container, then drill into nested array
      if (Array.isArray(currentData)) {
        currentData = currentData[0];
      }

      if (!isObject(currentData)) {
        return undefined;
      }

      const nestedArray = currentData[dataKey];
      if (!Array.isArray(nestedArray) || nestedArray.length === 0) {
        return undefined;
      }
      currentData = nestedArray[activeIndex] ?? nestedArray[0];
    } else {
      // No dataKey: index directly into the current array using activeIndex
      if (Array.isArray(currentData)) {
        currentData = currentData[activeIndex] ?? currentData[0];
      }
    }
  }

  // If the final result is still an array (e.g. no chain levels), return the first item
  if (Array.isArray(currentData)) {
    currentData = currentData[0];
  }

  return currentData;
};

/**
 * Gets the array of data that a loop step is iterating over.
 * This is the full array (not just the current item) that the loop will iterate through.
 *
 * @param {Object} output - The original data source output
 * @param {Array} chain - Array of { dataKey, activeIndex } from resolveIterateChain
 * @returns {Array} The array being iterated over by the last loop in the chain
 */
export const getIteratingArray = (output, chain) => {
  if (!output?.data || !Array.isArray(output.data) || output.data.length === 0) {
    return [];
  }

  if (chain.length === 0) {
    return output.data;
  }

  // Get the parent data (one level up from the current loop)
  const parentChain = chain.slice(0, -1);
  const currentLoop = chain[chain.length - 1];

  // Special case: top-level loop with no dataKey means "iterate over entire result"
  if (parentChain.length === 0 && !currentLoop.dataKey) {
    // Check for metadata total - handles paginated results where output.data
    // only contains the current page but meta has the full count
    const metaTotal = output?.meta?.output?.total || output?.meta?.output?.length || output?.meta?.output?.preProcessingLength;
    if (metaTotal && metaTotal > output.data.length) {
      // Create a sparse array with the correct length for iteration count purposes
      const sparseArray = new Array(metaTotal);
      for (let i = 0; i < output.data.length; i++) {
        sparseArray[i] = output.data[i];
      }
      return sparseArray;
    }
    return output.data;
  }

  let parentData;
  if (parentChain.length === 0) {
    // The current loop is at the top level, so parent is the raw output
    parentData = output.data[0];
  } else {
    parentData = drillDownNestedData(output, parentChain);
  }

  if (!isObject(parentData)) {
    // Check if parentData is an array - handle "entire result" selection for nested loops
    if (Array.isArray(parentData)) {
      return parentData;
    }
    return [];
  }

  // If the current loop has a dataKey, return that nested array
  if (currentLoop.dataKey) {
    const nestedArray = parentData[currentLoop.dataKey];

    if (!Array.isArray(nestedArray)) {
      return output.data;
    }
    return nestedArray;
  }

  // If no dataKey, we're iterating over the parent data directly
  return Array.isArray(parentData) ? parentData : [];
};

/**
 * Convenience function that resolves a step's iterate chain and drills down to get the current data.
 *
 * @param {string} stepId - The iterate step ID
 * @param {Array} steps - All workflow steps
 * @param {Array} apps - All apps metadata
 * @param {Object} stepOutputs - Object mapping step IDs to their outputs
 * @param {Object} options - Options passed to drillDownNestedData
 * @returns {Object} { data, iteratingArray, chain, originalStepId, originalOutput }
 */
export const resolveNestedIterateData = (stepId, steps, apps, stepOutputs, options = {}) => {
  const { chain, originalStepId } = resolveIterateChain(stepId, steps, apps);
  const originalOutput = originalStepId ? stepOutputs[originalStepId] : undefined;

  if (!originalOutput) {
    return {
      data: undefined,
      iteratingArray: [],
      chain,
      originalStepId,
      originalOutput: undefined
    };
  }

  const data = drillDownNestedData(originalOutput, chain, options);
  const iteratingArray = getIteratingArray(originalOutput, chain);

  return {
    data,
    iteratingArray,
    chain,
    originalStepId,
    originalOutput
  };
};

export default resolveNestedIterateData;
